/**
 * Chat screen -- keyword-based medication Q&A with voice integration.
 *
 * Features:
 * - Drug quick-select buttons for each patient medication
 * - Mic button for voice input (uses STT bridge)
 * - Auto-speaks all assistant responses via TTS
 * - Listens for voice transcript events from other screens
 * - Full intent parsing and drug database lookup
 *
 * Zero network calls. Everything runs on-device.
 */

import type { Drug, PatientMedication } from '../../types/index';
import { getPatientMedications, type PatientMedRecord } from '../../core/db';
import { searchDrugsByName, getDrugById } from '../../core/drug-store';
import { checkInteraction, checkFoodInteraction } from '../../core/interaction-graph';
import { speak, stop as stopSpeech, isSpeaking } from '../../voice/tts';
import { t } from '../../i18n';
import { queryRAG } from '../../core/rag-pipeline';
import { isLLMReady, getLLMStatus } from '../../core/llm-engine';
import { buildTemplateResponse } from '../../core/template-responses';
import { logAction } from '../../core/audit-log';
import { embed } from '../../core/embedding-engine';

// ---------------------------------------------------------------------------
// Drug color map (consistent with home screen)
// ---------------------------------------------------------------------------

const DRUG_COLORS: Record<string, string> = {
  metformin: '#3d9d8f',
  lisinopril: '#4a90d9',
  atorvastatin: '#d4a04a',
  omeprazole: '#9b6bb0',
  amlodipine: '#e07b5c',
  losartan: '#5a8f7b',
  levothyroxine: '#c27878',
  hydrochlorothiazide: '#6ba3c7',
};

function getDrugColor(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, color] of Object.entries(DRUG_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return '#3d9d8f';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  hasWarning: boolean;
}

// ---------------------------------------------------------------------------
// Patient data helpers
// ---------------------------------------------------------------------------

function getPatientMeds(): PatientMedication[] {
  return getPatientMedications().map((rec: PatientMedRecord) => ({
    id: rec.id,
    drug_rxcui: rec.drug_rxcui,
    drug_name: rec.drug_name,
    strength: rec.strength,
    frequency: rec.frequency as PatientMedication['frequency'],
    times: rec.times,
    instructions: rec.instructions,
    active: rec.active === 1,
  }));
}

// ---------------------------------------------------------------------------
// Drug resolution -- find a Drug from user text
// ---------------------------------------------------------------------------

async function resolveDrug(
  name: string,
  patientMeds: PatientMedication[],
): Promise<Drug | null> {
  const lower = name.toLowerCase().trim();

  for (const med of patientMeds) {
    const medLower = med.drug_name.toLowerCase();
    if (medLower.includes(lower) || lower.includes(medLower.split(' ')[0])) {
      const drug = await getDrugById(med.drug_rxcui);
      if (drug) return drug;
    }
  }

  const results = await searchDrugsByName(lower);
  return results[0] ?? null;
}

// ---------------------------------------------------------------------------
// Intent parsing
// ---------------------------------------------------------------------------

type ChatIntent =
  | { type: 'side_effects'; drugName: string }
  | { type: 'drug_interaction'; drugA: string; drugB: string }
  | { type: 'food_interaction'; food: string; drugName?: string }
  | { type: 'description'; drugName: string }
  | { type: 'dosage'; drugName: string }
  | { type: 'missed_dose'; drugName: string }
  | { type: 'my_medications' }
  | { type: 'schedule'; drugName: string }
  | { type: 'next_dose' }
  | { type: 'show_emr' }
  | { type: 'emr_diagnosis' }
  | { type: 'lab_question' }
  | { type: 'emr_follow_up' }
  | { type: 'emr_discharge_info' }
  | { type: 'emr_allergies' }
  | { type: 'why_taking'; drugName: string }
  | { type: 'help' }
  | { type: 'symptom_concern' }
  | { type: 'emotional_concern' }
  | { type: 'diet_question' }
  | { type: 'otc_safe' }
  | { type: 'warning_signs' }
  | { type: 'stop_taking'; drugName: string }
  | { type: 'overall_status' }
  | { type: 'hospitalization_reason' }
  | { type: 'unknown' };

function extractTwoDrugs(text: string): { a: string; b: string } | null {
  const withPattern = /(?:take|mix|combine|use)\s+(.+?)\s+(?:with|and)\s+(.+?)(?:\?|$)/i;
  const match = text.match(withPattern);
  if (match) return { a: match[1].trim(), b: match[2].trim() };

  const betweenPattern = /(?:interaction|interactions)\s+(?:between|of)\s+(.+?)\s+(?:and|with)\s+(.+?)(?:\?|$)/i;
  const match2 = text.match(betweenPattern);
  if (match2) return { a: match2[1].trim(), b: match2[2].trim() };

  return null;
}

function extractDrugName(text: string): string {
  const patterns = [
    /side effects?\s+(?:of|for|from)\s+(.+?)(?:\?|$)/i,
    /what (?:does|is|are)\s+(.+?)(?:\s+(?:do|for|used for))?\s*\??$/i,
    /(?:how|when)\s+(?:do I|should I|to)\s+take\s+(.+?)(?:\?|$)/i,
    /(?:tell me about|info on|about|describe)\s+(.+?)(?:\?|$)/i,
    /(?:missed?\s+(?:a\s+)?dose\s+(?:of|for))\s+(.+?)(?:\?|$)/i,
    /(?:when\s+(?:do I|should I)\s+take)\s+(.+?)(?:\?|$)/i,
    /(?:schedule\s+for)\s+(.+?)(?:\?|$)/i,
    /(?:dosage|dose)\s+(?:of|for|instructions?)\s+(.+?)(?:\?|$)/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return match[1]
        .replace(/[?.!,]+$/, '')
        .replace(/\b(please|the|my|some)\b/gi, '')
        .trim();
    }
  }
  return '';
}

function parseIntent(text: string): ChatIntent {
  const lower = text.toLowerCase().trim();

  // --- Warning signs / when to go to ER ---
  if (/\b(?:emergency|er\b|emergency\s+room|call\s+911|go\s+(?:back\s+)?to\s+(?:the\s+)?(?:hospital|er|emergency))\b/.test(lower) ||
      /\b(?:warning\s+sign|watch\s+(?:out|for)|red\s+flag|danger\s+sign|when\s+should\s+i\s+(?:worry|call|go))\b/.test(lower)) {
    return { type: 'warning_signs' };
  }

  // --- Overall health status ---
  if (/\bhow\s+(?:am\s+i|are\s+things)\s+(?:doing|going|looking)\b/.test(lower) ||
      /\b(?:my\s+)?(?:progress|overall|getting\s+better)\b/.test(lower) ||
      /\bhow\s+(?:is|are)\s+my\s+(?:health|recovery|numbers)\b/.test(lower)) {
    return { type: 'overall_status' };
  }

  // --- Why was I in the hospital ---
  if (/\bwhy\s+(?:was\s+i|did\s+i\s+(?:go|end\s+up))\s+(?:in\s+(?:the\s+)?)?(?:hospital|admitted)\b/.test(lower) ||
      /\b(?:don'?t|do\s+not)\s+understand\s+why\s+i\s+was\s+(?:in|at)\s+(?:the\s+)?hospital\b/.test(lower) ||
      /\bwhat\s+(?:happened|was\s+wrong)\s+(?:to\s+me|when\s+i\s+(?:went|was))\b/.test(lower)) {
    return { type: 'hospitalization_reason' };
  }

  // --- OTC medication safety (BEFORE symptom concern — "can I take ibuprofen" is OTC, not symptom) ---
  if (/\b(?:can\s+i\s+take|is\s+it\s+(?:safe|okay)\s+to\s+take|should\s+i\s+take)\b/.test(lower) &&
      /\b(?:ibuprofen|advil|motrin|tylenol|acetaminophen|aspirin|naproxen|aleve|tums|pepto|benadryl|claritin|zyrtec|mucinex|sudafed)\b/.test(lower)) {
    return { type: 'otc_safe' };
  }

  // --- Symptom concerns (tingling, dizzy, pain, nausea, etc.) ---
  // Physical symptom keywords. When a physical symptom is present, this takes
  // priority over emotional_concern (e.g. "my feet are tingling, should I be worried"
  // is a symptom question, not an emotional one).
  const hasPhysicalSymptom =
      /\b(?:tingling|numbness|numb|pins\s+and\s+needles|burning\s+(?:feeling|sensation))\b/.test(lower) ||
      /\b(?:dizz(?:y|iness)|lightheaded|faint(?:ing)?|vertigo)\b/.test(lower) ||
      /\b(?:stomach\s+(?:hurts?|ache|pain|upset|cramp)|nausea|nauseous|vomit|throwing\s+up)\b/.test(lower) ||
      /\b(?:headache|muscle\s+(?:pain|ache|weakness)|joint\s+pain|swelling|swollen)\b/.test(lower) ||
      /\b(?:rash|itching|itchy|hives|blurred?\s+vision|trouble\s+(?:seeing|breathing))\b/.test(lower) ||
      /\b(?:chest\s+(?:pain|tight|pressure)|shortness\s+of\s+breath|heart\s+(?:racing|pounding))\b/.test(lower);

  if (hasPhysicalSymptom ||
      /\b(?:should\s+i\s+(?:be\s+)?worried|is\s+(?:this|that|it)\s+(?:normal|okay|serious|dangerous))\b/.test(lower)) {
    // Only route to symptom_concern if there is a physical symptom keyword OR the
    // worry phrasing appears without a clearly emotional context
    if (hasPhysicalSymptom) {
      return { type: 'symptom_concern' };
    }
  }

  // --- Emotional / psychosocial concerns (AFTER symptom check) ---
  if (/\b(?:scared|afraid|worried|anxious|nervous|terrified|frightened)\b/.test(lower) ||
      /\b(?:overwhelmed|depressed|sad|hopeless|can'?t\s+cope|too\s+much)\b/.test(lower) ||
      /\b(?:feel(?:ing)?\s+(?:down|alone|lost|confused|frustrated))\b/.test(lower)) {
    return { type: 'emotional_concern' };
  }

  // --- Diet questions ---
  if (/\b(?:can\s+i\s+eat|should\s+i\s+eat|what\s+(?:can|should)\s+i\s+eat|is\s+.+\s+(?:okay|safe|good)\s+(?:to\s+eat|for\s+me))\b/.test(lower) ||
      /\b(?:diet|what\s+(?:to|should\s+i)\s+(?:eat|avoid)|food\s+(?:i\s+(?:can|should)|to\s+(?:eat|avoid)))\b/.test(lower) ||
      /\b(?:rice|bread|sugar|carbs?|pasta|fruit|sweets?|candy|cake|soda|juice)\b/.test(lower) && /\b(?:can\s+i|okay|allowed|safe|eat|have|drink)\b/.test(lower)) {
    return { type: 'diet_question' };
  }

  // --- Stop taking / take forever ---
  if (/\b(?:stop\s+taking|quit\s+taking|don'?t\s+(?:want\s+to\s+)?take|tired\s+of\s+taking)\b/.test(lower) ||
      /\b(?:take\s+(?:these|this|them|pills?|meds?)\s+forever)\b/.test(lower) ||
      /\bwhat\s+(?:happens?|if)\s+(?:i\s+)?(?:stop|quit|don'?t\s+take)\b/.test(lower) ||
      /\b(?:have\s+to|going\s+to\s+have\s+to)\s+take\s+(?:these|this|them|pills?|meds?).*\b(?:forever|always|rest\s+of)\b/.test(lower) ||
      /\b(?:do\s+i\s+(?:really\s+)?(?:need|have\s+to)\s+take)\b/.test(lower) && /\b(?:forever|always|rest\s+of\s+my\s+life|every\s+day)\b/.test(lower)) {
    return { type: 'stop_taking', drugName: extractDrugName(lower) };
  }

  // --- Why do I need so many pills ---
  if (/\bwhy\s+(?:do\s+i\s+(?:need|have|take)|so\s+many)\s+(?:so\s+many\s+)?(?:pills?|meds?|medications?|medicines?)\b/.test(lower)) {
    return { type: 'why_taking', drugName: '' };
  }

  // Medication list queries
  if (
    /\b(my\s+(?:meds|medications?|medicines?|drugs?|prescriptions?))\b/.test(lower) ||
    /\b(list|show|all)\s+(?:my\s+)?(?:meds|medications?|medicines?)\b/.test(lower) ||
    /\bwhat\s+(?:medications?|meds|medicines?|drugs?)\s+(?:am\s+i|do\s+i)\b/.test(lower) ||
    /\bwhat\s+(?:am\s+i|do\s+i)\s+(?:take|taking)\b/.test(lower)
  ) {
    return { type: 'my_medications' };
  }

  // Next dose
  if (/\bnext\s+(?:dose|pill|med|medication)\b/.test(lower) ||
      /\bwhat\s+(?:time|do\s+i\s+take)\s+(?:is\s+)?(?:my\s+)?next\b/.test(lower)) {
    return { type: 'next_dose' };
  }

  // Schedule / when to take
  if (/\b(?:schedule|when.*take|what\s+time)\b/.test(lower)) {
    return { type: 'schedule', drugName: extractDrugName(lower) };
  }

  // EMR / health record
  if (/\b(?:my\s+)?(?:health\s+)?record\b/.test(lower) ||
      /\bshow\s+(?:my\s+)?(?:emr|medical\s+record)\b/.test(lower)) {
    return { type: 'show_emr' };
  }

  // Diagnoses / conditions (but NOT emotional statements about conditions)
  if (/\b(?:what\s+(?:do\s+i\s+have|is\s+wrong|are\s+my\s+(?:diagnos|condition)))\b/.test(lower) ||
      /\b(?:my\s+)?diagnos(?:es|is)\b/.test(lower) ||
      /\blist\s+(?:my\s+)?conditions?\b/.test(lower)) {
    return { type: 'emr_diagnosis' };
  }

  // Allergies
  if (/\ballergi/.test(lower)) {
    return { type: 'emr_allergies' };
  }

  // Lab results
  if (/\b(?:lab|hba1c|a1c|ldl|hdl|cholesterol|glucose|blood\s+(?:sugar|test)|test\s+results?)\b/.test(lower)) {
    return { type: 'lab_question' };
  }

  // Follow-up / appointments
  if (/\b(?:appointment|follow.?up|next\s+(?:visit|doctor)|when.*see.*doctor)\b/.test(lower)) {
    return { type: 'emr_follow_up' };
  }

  // Discharge info
  if (/\b(?:discharge|hospital\s+stay|when.*(?:admitted|hospitali))\b/.test(lower)) {
    return { type: 'emr_discharge_info' };
  }

  // Drug interactions
  const twoDrugs = extractTwoDrugs(lower);
  if (twoDrugs) {
    return { type: 'drug_interaction', drugA: twoDrugs.a, drugB: twoDrugs.b };
  }

  // Alcohol interaction — only extract drug name if one is explicitly mentioned
  if (/\b(alcohol|beer|wine|liquor|drink)\b/.test(lower)) {
    const drugName = extractDrugName(lower);
    // Don't use fallback regex — it extracts non-drug words like "dinner"
    return { type: 'food_interaction', food: 'alcohol', drugName: drugName || undefined };
  }

  // Grapefruit/citrus interaction
  if (/\b(grapefruit|citrus|orange juice)\b/.test(lower)) {
    const food = lower.match(/\b(grapefruit|citrus|orange juice)\b/i)?.[1] ?? 'grapefruit';
    const drugName = extractDrugName(lower);
    return { type: 'food_interaction', food, drugName: drugName || undefined };
  }

  // Food interaction (specific drug + food combo)
  if (/\b(eat|food|dairy|milk|calcium)\b/.test(lower) && /\b(with|while|interaction)\b/.test(lower)) {
    const food = lower.match(/\b(dairy|milk|calcium|food)\b/i)?.[1] ?? 'food';
    const drugName = extractDrugName(lower);
    return { type: 'food_interaction', food, drugName: drugName || undefined };
  }

  // Side effects
  if (/\bside\s+effects?\b/.test(lower)) {
    return { type: 'side_effects', drugName: extractDrugName(lower) };
  }

  // Missed dose (including "forgot")
  if (/\b(?:miss(?:ed)?|forgot|forgotten|skipped)\s+(?:a\s+|my\s+)?(?:dose|pill|medication|med|evening|morning|afternoon)\b/.test(lower)) {
    return { type: 'missed_dose', drugName: extractDrugName(lower) };
  }

  // Why am I taking this
  if (/\bwhy\s+(?:am\s+i|do\s+i)\s+(?:take|taking|on|need)\b/.test(lower)) {
    return { type: 'why_taking', drugName: extractDrugName(lower) };
  }

  // Dosage / how to take
  if (/\b(?:how|dosage|dose|instructions?)\b/.test(lower) && /\btake\b/.test(lower)) {
    return { type: 'dosage', drugName: extractDrugName(lower) };
  }

  // Drug description
  if (/\b(?:what\s+(?:does|is)|tell\s+me\s+about|describe|info)\b/.test(lower)) {
    const drugName = extractDrugName(lower);
    if (drugName) return { type: 'description', drugName };
  }

  // Help
  if (/^(?:help|what\s+can\s+you\s+(?:do|help)|how\s+(?:does|do)\s+(?:this|you)\s+work)\b/.test(lower)) {
    return { type: 'help' };
  }

  return { type: 'unknown' };
}

// ---------------------------------------------------------------------------
// Response generation
// ---------------------------------------------------------------------------

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function formatBulletList(items: string[], limit: number = 8): string {
  const shown = items.slice(0, limit);
  let result = shown.map((item) => `  - ${item}`).join('\n');
  if (items.length > limit) {
    result += `\n  ${t('chat.andMore', { count: items.length - limit })}`;
  }
  return result;
}

async function generateResponse(
  intent: ChatIntent,
  patientMeds: PatientMedication[],
): Promise<{ text: string; hasWarning: boolean }> {
  switch (intent.type) {
    case 'my_medications': {
      if (patientMeds.length === 0) {
        return { text: t('chat.noMedsOnFile'), hasWarning: false };
      }
      const lines = patientMeds.map(
        (m) => `  - ${m.drug_name} (${m.strength}) -- ${m.times.map(formatTime12h).join(', ')}`,
      );
      return {
        text: `${t('chat.takingCount', { count: patientMeds.length, s: patientMeds.length > 1 ? 's' : '' })}\n\n${lines.join('\n')}`,
        hasWarning: false,
      };
    }

    case 'side_effects': {
      if (!intent.drugName) {
        return { text: t('chat.whichSideEffects'), hasWarning: false };
      }
      const drug = await resolveDrug(intent.drugName, patientMeds);
      if (!drug) {
        return { text: t('chat.drugNotFound', { drug: intent.drugName }), hasWarning: false };
      }
      if (drug.side_effects.length === 0) {
        return { text: t('chat.noSideEffectsListed', { drug: drug.generic_name }), hasWarning: false };
      }
      return {
        text: `${t('chat.sideEffectsOf', { drug: drug.generic_name })}\n\n${formatBulletList(drug.side_effects)}\n\n${t('chat.sideEffectsAdvice')}`,
        hasWarning: true,
      };
    }

    case 'drug_interaction': {
      const drugA = await resolveDrug(intent.drugA, patientMeds);
      const drugB = await resolveDrug(intent.drugB, patientMeds);
      if (!drugA || !drugB) {
        const missing = !drugA ? intent.drugA : intent.drugB;
        return { text: t('chat.drugNotFoundShort', { drug: missing }), hasWarning: false };
      }
      const interaction = await checkInteraction(drugA.id, drugB.id);
      if (interaction) {
        const severityLabel = interaction.severity.charAt(0).toUpperCase() + interaction.severity.slice(1);
        return {
          text: `${t('chat.interactionWarning', { drugA: drugA.generic_name, drugB: drugB.generic_name })}\n\n${t('chat.severity', { level: severityLabel })}\n${interaction.description}\n\n${t('chat.interactionAdvice')}`,
          hasWarning: true,
        };
      }
      const { findInteraction: findIx } = await import('../../core/db');
      const ixRecord = findIx(drugA.id, drugB.id);
      if (ixRecord) {
        const severityLabel = ixRecord.severity.charAt(0).toUpperCase() + ixRecord.severity.slice(1);
        return {
          text: `${t('chat.interactionWarning', { drugA: drugA.generic_name, drugB: drugB.generic_name })}\n\n${t('chat.severity', { level: severityLabel })}\n${ixRecord.description}\n\n${t('chat.interactionAdvice')}`,
          hasWarning: true,
        };
      }
      return {
        text: `${t('chat.noInteractionFound', { drugA: drugA.generic_name, drugB: drugB.generic_name })}\n\n${t('chat.noInteractionDisclaimer')}`,
        hasWarning: false,
      };
    }

    case 'food_interaction': {
      if (intent.drugName) {
        const drug = await resolveDrug(intent.drugName, patientMeds);
        if (!drug) {
          return { text: t('chat.drugNotFoundShort', { drug: intent.drugName }), hasWarning: false };
        }
        const match = await checkFoodInteraction(drug.id, intent.food);
        if (match) {
          return { text: `${t('chat.foodInteractionWarning', { drug: drug.generic_name, food: intent.food })}\n\n${match}\n\n${t('chat.foodInteractionAdvice')}`, hasWarning: true };
        }
        return { text: `${t('chat.noFoodInteraction', { food: intent.food, drug: drug.generic_name })}\n\n${t('chat.askPharmacist')}`, hasWarning: false };
      }
      const found: string[] = [];
      for (const med of patientMeds) {
        const match = await checkFoodInteraction(med.drug_rxcui, intent.food);
        if (match) found.push(`${med.drug_name}: ${match}`);
      }
      if (found.length > 0) {
        return { text: `${t('chat.someMedsInteractFood', { food: intent.food })}\n\n${formatBulletList(found)}\n\n${t('chat.foodInteractionAdvice')}`, hasWarning: true };
      }
      return { text: `${t('chat.noFoodInteractionAny', { food: intent.food })}\n\n${t('chat.askPharmacist')}`, hasWarning: false };
    }

    case 'description': {
      if (!intent.drugName) {
        return { text: t('chat.whichDescription'), hasWarning: false };
      }
      const drug = await resolveDrug(intent.drugName, patientMeds);
      if (!drug) {
        return { text: t('chat.drugNotFound', { drug: intent.drugName }), hasWarning: false };
      }
      const desc = drug.description || t('chat.noDescription');
      return { text: `${drug.generic_name}: ${desc}`, hasWarning: false };
    }

    case 'dosage': {
      if (!intent.drugName) {
        return { text: t('chat.whichDosage'), hasWarning: false };
      }
      const drug = await resolveDrug(intent.drugName, patientMeds);
      if (!drug) {
        return { text: t('chat.drugNotFoundShort', { drug: intent.drugName }), hasWarning: false };
      }
      const instructions = drug.dosage_instructions || t('chat.noDosageInstructions');
      return { text: `${t('chat.howToTake', { drug: drug.generic_name })}\n\n${instructions}`, hasWarning: false };
    }

    case 'missed_dose': {
      if (!intent.drugName) {
        return { text: t('chat.whichMissedDose'), hasWarning: false };
      }
      const drug = await resolveDrug(intent.drugName, patientMeds);
      if (!drug) {
        return { text: t('chat.drugNotFoundShort', { drug: intent.drugName }), hasWarning: false };
      }
      const advice = drug.missed_dose || t('chat.noMissedDoseGuidance');
      return { text: `${t('chat.ifMissedDose', { drug: drug.generic_name })}\n\n${advice}`, hasWarning: true };
    }

    case 'schedule': {
      if (!intent.drugName) {
        if (patientMeds.length === 0) return { text: t('chat.noMedsSchedule'), hasWarning: false };
        const lines = patientMeds.map((m) => `  - ${m.drug_name}: ${m.times.map(formatTime12h).join(', ')}`);
        return { text: `${t('chat.yourSchedule')}\n\n${lines.join('\n')}`, hasWarning: false };
      }
      const lower = intent.drugName.toLowerCase();
      const med = patientMeds.find((m) => m.drug_name.toLowerCase().includes(lower));
      if (med) {
        return {
          text: `${med.drug_name} (${med.strength}):\n\n${t('chat.scheduleLabel', { times: med.times.map(formatTime12h).join(', ') })}\n${t('chat.scheduleFrequency', { frequency: med.frequency.replace(/_/g, ' ') })}\n\n${med.instructions || ''}`,
          hasWarning: false,
        };
      }
      return { text: t('chat.drugNotInList', { drug: intent.drugName }), hasWarning: false };
    }

    case 'unknown':
    default: {
      // If LLM is loading, show a helpful message about what they can ask
      const llmStatus = getLLMStatus();
      if (llmStatus === 'loading') {
        return {
          text: t('chat.gettingReady'),
          hasWarning: false,
        };
      }
      return {
        text: t('chat.unknownHelp'),
        hasWarning: false,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// DOM rendering
// ---------------------------------------------------------------------------

export async function renderChatScreen(container: HTMLElement): Promise<void> {
  container.textContent = '';
  container.className = 'screen';
  container.style.padding = '0';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.overflow = 'hidden';

  const messages: ChatMessage[] = [];
  let patientMeds: PatientMedication[] = [];
  const cleanups: (() => void)[] = [];

  // Track selected drug for context
  let selectedDrug: string | null = null;

  try {
    patientMeds = getPatientMeds();
  } catch {
    // DB not ready
  }

  // ---- Header with title ----
  const header = document.createElement('header');
  header.style.cssText =
    'flex-shrink: 0; border-bottom: 1px solid var(--color-border); padding: var(--space-sm) var(--space-lg);';

  const headerTitle = document.createElement('h2');
  headerTitle.textContent = t('chat.title');
  headerTitle.style.cssText = 'font-size: var(--font-size-large); margin: 0; color: var(--color-text);';
  header.appendChild(headerTitle);

  const headerSub = document.createElement('p');
  headerSub.textContent = t('chat.subtitle');
  headerSub.style.cssText = 'font-size: var(--font-size-small); color: var(--color-text-muted); margin: 2px 0 0 0;';
  header.appendChild(headerSub);

  container.appendChild(header);

  // ---- Drug quick-select buttons ----
  if (patientMeds.length > 0) {
    const drugBar = document.createElement('div');
    drugBar.style.cssText =
      'flex-shrink: 0; display: flex; gap: 8px; padding: var(--space-sm) var(--space-lg); overflow-x: auto; border-bottom: 1px solid var(--color-border); -webkit-overflow-scrolling: touch;';

    for (const med of patientMeds) {
      const pill = document.createElement('button');
      const color = getDrugColor(med.drug_name);
      pill.style.cssText = `
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 16px;
        border-radius: 20px;
        border: 2px solid ${color};
        background: ${color}11;
        color: var(--color-text);
        font-family: var(--font-family);
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        touch-action: manipulation;
        transition: all 0.15s ease;
        white-space: nowrap;
        min-height: 44px;
      `;

      // Colored dot
      const dot = document.createElement('span');
      dot.style.cssText = `width: 10px; height: 10px; border-radius: 50%; background: ${color}; flex-shrink: 0;`;
      pill.appendChild(dot);

      const label = document.createElement('span');
      label.textContent = med.drug_name.split(' ')[0]; // First word only for compactness
      pill.appendChild(label);

      pill.addEventListener('click', () => {
        selectedDrug = med.drug_name;
        // Highlight selected pill
        drugBar.querySelectorAll('button').forEach(b => {
          (b as HTMLElement).style.background = `${getDrugColor((b.querySelector('span:last-child') as HTMLElement)?.textContent || '')}11`;
          (b as HTMLElement).style.fontWeight = '600';
        });
        pill.style.background = `${color}33`;
        pill.style.fontWeight = '800';
        // Show quick action menu for this drug
        showDrugQuickActions(med.drug_name);
      });

      drugBar.appendChild(pill);
    }

    container.appendChild(drugBar);
  }

  // ---- Messages area ----
  const messagesArea = document.createElement('div');
  messagesArea.setAttribute('role', 'log');
  messagesArea.setAttribute('aria-label', t('chat.ariaConversation'));
  messagesArea.setAttribute('aria-live', 'polite');
  messagesArea.setAttribute('aria-relevant', 'additions');
  messagesArea.style.cssText =
    'flex: 1; overflow-y: auto; padding: var(--space-md); display: flex; flex-direction: column; gap: var(--space-sm); -webkit-overflow-scrolling: touch;';
  container.appendChild(messagesArea);

  // ---- Input area with mic button ----
  const inputArea = document.createElement('div');
  inputArea.style.cssText =
    'flex-shrink: 0; display: flex; align-items: center; gap: var(--space-sm); padding: var(--space-sm) var(--space-md); padding-bottom: calc(var(--nav-height) + var(--safe-bottom) + var(--space-sm)); border-top: 1px solid var(--color-border); background: var(--color-bg-elevated);';
  container.appendChild(inputArea);

  // Mic button
  const micBtn = document.createElement('button');
  micBtn.setAttribute('aria-label', t('chat.ariaHoldToSpeak'));
  micBtn.style.cssText = `
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--color-primary);
    color: #ffffff;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    transition: background-color 0.15s ease, transform 0.15s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  `;
  micBtn.textContent = '\uD83C\uDF99'; // mic emoji
  inputArea.appendChild(micBtn);

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = t('chat.placeholder');
  input.setAttribute('aria-label', t('chat.ariaInputPlaceholder'));
  input.setAttribute('autocomplete', 'off');
  input.style.cssText =
    'flex: 1; min-height: 48px; padding: var(--space-sm) var(--space-md); border: 2px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font-family: var(--font-family); font-size: var(--font-size-body); outline: none; transition: border-color var(--transition-fast);';
  input.addEventListener('focus', () => { input.style.borderColor = 'var(--color-accent)'; });
  input.addEventListener('blur', () => { input.style.borderColor = 'var(--color-border)'; });
  inputArea.appendChild(input);

  const sendBtn = document.createElement('button');
  sendBtn.className = 'btn btn-primary';
  sendBtn.setAttribute('aria-label', t('chat.send'));
  sendBtn.textContent = t('chat.send');
  sendBtn.style.cssText = 'min-height: 48px; flex-shrink: 0;';
  inputArea.appendChild(sendBtn);

  // ---- Mic button behavior (push-to-talk) ----
  let micPressing = false;
  let micState: 'idle' | 'listening' | 'processing' = 'idle';

  function setMicState(state: 'idle' | 'listening' | 'processing') {
    micState = state;
    switch (state) {
      case 'idle':
        micBtn.style.backgroundColor = 'var(--color-primary)';
        micBtn.style.transform = '';
        micBtn.textContent = '\uD83C\uDF99';
        break;
      case 'listening':
        micBtn.style.backgroundColor = '#ef4444';
        micBtn.style.transform = 'scale(1.15)';
        micBtn.textContent = '\uD83C\uDF99';
        break;
      case 'processing':
        micBtn.style.backgroundColor = 'var(--color-primary)';
        micBtn.style.transform = '';
        micBtn.textContent = '\u23F3'; // hourglass
        break;
    }
  }

  micBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (micState === 'processing') return;
    micPressing = true;
    setMicState('listening');
    // Unlock TTS on this user gesture — speak a silent utterance so the
    // browser allows speechSynthesis.speak() later without a gesture
    if ('speechSynthesis' in window) {
      const silent = new SpeechSynthesisUtterance('');
      silent.volume = 0;
      window.speechSynthesis.speak(silent);
    }
    document.dispatchEvent(new CustomEvent('aftercare:voice-start'));
  });

  const handleMicRelease = (e: Event) => {
    e.preventDefault();
    if (!micPressing) return;
    micPressing = false;
    setMicState('processing');
    document.dispatchEvent(new CustomEvent('aftercare:voice-stop'));
  };

  micBtn.addEventListener('pointerup', handleMicRelease);
  micBtn.addEventListener('pointercancel', handleMicRelease);
  micBtn.addEventListener('pointerleave', (e) => { if (micPressing) handleMicRelease(e); });
  micBtn.addEventListener('contextmenu', (e) => e.preventDefault());

  // ---- Welcome message ----
  const welcomeText = patientMeds.length > 0
    ? t('chat.welcomeWithMeds', { n: patientMeds.length, s: patientMeds.length > 1 ? 's' : '' })
    : t('chat.welcomeNoMeds');

  addMessage('assistant', welcomeText, false, false); // Don't auto-speak welcome

  // LLM loads silently in background — no status messages shown to patient

  // ---- Quick action chips when a drug is selected ----
  function showDrugQuickActions(drugName: string): void {
    // Remove any existing quick actions
    const existing = messagesArea.querySelector('.drug-quick-actions');
    if (existing) existing.remove();

    const actions = document.createElement('div');
    actions.className = 'drug-quick-actions';
    actions.style.cssText = 'display: flex; flex-wrap: wrap; gap: 6px; padding: var(--space-xs) 0;';

    const questions = [
      t('chat.quick.whatDoes', { drug: drugName }),
      t('chat.quick.sideEffects', { drug: drugName }),
      t('chat.quick.howTake', { drug: drugName }),
      t('chat.quick.missedDose', { drug: drugName }),
      t('chat.quick.whenTake', { drug: drugName }),
      t('chat.quick.alcohol', { drug: drugName }),
    ];

    for (const q of questions) {
      const chip = document.createElement('button');
      chip.style.cssText = `
        padding: 12px 14px;
        border-radius: 16px;
        border: 1px solid rgba(61,157,143,0.3);
        background: rgba(61,157,143,0.06);
        color: var(--color-primary);
        font-size: 14px;
        font-family: var(--font-family);
        cursor: pointer;
        touch-action: manipulation;
        line-height: 1.3;
        transition: all 0.15s ease;
        min-height: 44px;
        display: flex;
        align-items: center;
        text-align: left;
      `;
      chip.textContent = q;
      chip.addEventListener('click', () => {
        actions.remove();
        processQuestion(q);
      });
      actions.appendChild(chip);
    }

    messagesArea.appendChild(actions);
    requestAnimationFrame(() => { messagesArea.scrollTop = messagesArea.scrollHeight; });
  }

  // ---- Suggested questions (shown initially if no drug selected) ----
  if (patientMeds.length > 0) {
    const suggestionsContainer = document.createElement('div');
    suggestionsContainer.className = 'initial-suggestions';
    suggestionsContainer.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: var(--space-xs) 0;';

    const suggestions = [
      t('chat.suggest.myMeds'),
      t('chat.suggest.sideEffects'),
      t('chat.suggest.alcohol'),
      t('chat.suggest.missedDose'),
    ];

    for (const q of suggestions) {
      const chip = document.createElement('button');
      chip.style.cssText = 'padding: 12px 14px; border-radius: 16px; border: 1px solid rgba(61,157,143,0.3); background: rgba(61,157,143,0.06); color: var(--color-primary); font-size: 14px; font-family: var(--font-family); cursor: pointer; touch-action: manipulation; text-align: left; line-height: 1.3; min-height: 44px; display: flex; align-items: center;';
      chip.textContent = q;
      chip.addEventListener('click', () => {
        suggestionsContainer.remove();
        processQuestion(q);
      });
      suggestionsContainer.appendChild(chip);
    }
    messagesArea.appendChild(suggestionsContainer);
  }

  // ---- Core functions ----

  function addMessage(role: 'user' | 'assistant', text: string, hasWarning: boolean, autoSpeak: boolean = true): void {
    const msg: ChatMessage = { role, text, hasWarning };
    messages.push(msg);
    renderMessage(msg);
    requestAnimationFrame(() => { messagesArea.scrollTop = messagesArea.scrollHeight; });

    // Auto-speak assistant responses
    if (role === 'assistant' && autoSpeak && text.length > 0) {
      const spokenText = text
        .replace(/  - /g, ', ')
        .replace(/\n\n/g, '. ')
        .replace(/\n/g, '. ');
      stopSpeech();
      speak(spokenText).catch(() => {
        console.log('[Chat] TTS failed for response');
        // Show subtle "audio unavailable" indicator on the last assistant bubble
        const bubbles = messagesArea.querySelectorAll('[role="article"]');
        const lastBubble = bubbles[bubbles.length - 1];
        if (lastBubble) {
          const indicator = document.createElement('span');
          indicator.style.cssText = 'display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 12px; color: var(--color-text-muted); opacity: 0.7;';
          indicator.textContent = t('chat.audioUnavailable');
          lastBubble.appendChild(indicator);
        }
      });
    }
  }

  function renderMessage(msg: ChatMessage): void {
    const bubble = document.createElement('div');
    bubble.setAttribute('role', 'article');
    bubble.setAttribute('aria-label', msg.role === 'user' ? t('chat.ariaUserMessage') : t('chat.ariaAssistantMessage'));

    const isUser = msg.role === 'user';
    bubble.style.cssText = `
      max-width: 88%;
      padding: 12px 16px;
      border-radius: var(--radius-lg);
      font-size: var(--font-size-body);
      line-height: var(--line-height);
      word-wrap: break-word;
      white-space: pre-wrap;
      ${isUser
        ? 'align-self: flex-end; background: var(--color-accent); color: #ffffff; border-bottom-right-radius: 4px;'
        : 'align-self: flex-start; background: var(--color-bg-card); color: var(--color-text); border-left: 4px solid #2dd4bf; border-bottom-left-radius: 4px;'
      }
    `;

    if (msg.hasWarning && !isUser) {
      renderFormattedText(bubble, msg.text);
    } else {
      bubble.textContent = msg.text;
    }

    // Replay button on assistant messages
    if (!isUser) {
      const replayBtn = document.createElement('button');
      replayBtn.setAttribute('aria-label', t('chat.ariaReplay'));
      replayBtn.textContent = '\uD83D\uDD0A'; // speaker emoji
      replayBtn.style.cssText =
        'display: inline-flex; align-items: center; justify-content: center; margin-top: 8px; padding: 10px 14px; border-radius: 12px; border: 1px solid var(--color-border); background: transparent; font-size: 18px; cursor: pointer; touch-action: manipulation; min-height: 44px; min-width: 44px;';
      replayBtn.addEventListener('click', () => {
        if (isSpeaking()) {
          stopSpeech();
        } else {
          const spokenText = msg.text.replace(/  - /g, ', ').replace(/\n\n/g, '. ').replace(/\n/g, '. ');
          speak(spokenText);
        }
      });
      bubble.appendChild(replayBtn);
    }

    messagesArea.appendChild(bubble);
  }

  function renderFormattedText(parent: HTMLElement, text: string): void {
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^WARNING/i.test(line)) {
        const span = document.createElement('span');
        span.style.cssText = 'color: var(--color-danger); font-weight: 700;';
        span.textContent = line;
        parent.appendChild(span);
      } else if (/^Severity:\s*(Major|Contraindicated)/i.test(line)) {
        const span = document.createElement('span');
        span.style.cssText = 'color: var(--color-danger); font-weight: 700;';
        span.textContent = line;
        parent.appendChild(span);
      } else if (/^Severity:\s*Moderate/i.test(line)) {
        const span = document.createElement('span');
        span.style.cssText = 'color: var(--color-warning); font-weight: 700;';
        span.textContent = line;
        parent.appendChild(span);
      } else {
        parent.appendChild(document.createTextNode(line));
      }
      if (i < lines.length - 1) parent.appendChild(document.createTextNode('\n'));
    }
  }

  // ---- Process a question (from text input, voice, or chip tap) ----

  /**
   * Replace generic drug references ("this drug", "it", "this one", etc.)
   * with the currently selected drug name so the intent parser can find it.
   */
  function resolveContextualReferences(text: string): string {
    if (!selectedDrug) return text;

    const drugFirstWord = selectedDrug.split(' ')[0];

    // Patterns that refer to the selected drug generically
    const genericRefs = [
      /\b(this\s+(?:drug|medication|medicine|med|pill|one))\b/gi,
      /\b(that\s+(?:drug|medication|medicine|med|pill|one))\b/gi,
      /\b(the\s+(?:selected|highlighted|chosen)\s+(?:drug|medication|medicine|med|pill|one))\b/gi,
    ];

    let resolved = text;
    for (const pattern of genericRefs) {
      resolved = resolved.replace(pattern, drugFirstWord);
    }

    // Also handle bare "it" when it's clearly about the drug:
    // "What are the side effects of it?" / "Tell me about it" / "How do I take it?"
    if (/\b(?:side effects?|dosage|dose|interactions?|description|schedule|about|take)\s+(?:of\s+|for\s+|with\s+)?it\b/i.test(resolved)) {
      resolved = resolved.replace(/\bit\b/gi, drugFirstWord);
    }
    // "What does it do?" / "What is it for?"
    if (/\bwhat\s+(?:does|is)\s+it\b/i.test(resolved)) {
      resolved = resolved.replace(/\bit\b/gi, drugFirstWord);
    }

    return resolved;
  }

  async function processQuestion(text: string): Promise<void> {
    if (!text.trim()) return;

    addMessage('user', text, false, false);

    // Remove initial suggestions
    const suggestions = messagesArea.querySelector('.initial-suggestions');
    if (suggestions) suggestions.remove();

    // Typing indicator
    const typingBubble = document.createElement('div');
    typingBubble.style.cssText =
      'align-self: flex-start; padding: 12px 16px; background: var(--color-bg-card); border-radius: var(--radius-lg); border-left: 4px solid #2dd4bf; color: var(--color-text-dim); font-size: var(--font-size-body);';
    typingBubble.textContent = t('chat.lookingUp');
    messagesArea.appendChild(typingBubble);
    requestAnimationFrame(() => { messagesArea.scrollTop = messagesArea.scrollHeight; });

    try {
      try { patientMeds = getPatientMeds(); } catch { /* keep existing */ }

      // Resolve "this drug", "it", etc. to the selected drug name
      const resolvedText = resolveContextualReferences(text);
      let intent = parseIntent(resolvedText);

      // If a drug is selected but the intent has no drug name, fill it in
      if (selectedDrug) {
        const drugFirstWord = selectedDrug.split(' ')[0];
        if ('drugName' in intent && !intent.drugName) {
          (intent as { drugName: string }).drugName = drugFirstWord;
        }
        // If the user just types a bare question like "side effects?" with a drug selected
        if (intent.type === 'unknown' && selectedDrug) {
          const lower = resolvedText.toLowerCase().trim();
          if (/^side\s+effects?\??$/i.test(lower)) {
            intent = { type: 'side_effects', drugName: drugFirstWord };
          } else if (/^(?:how\s+(?:do\s+I\s+)?take|dosage|dose|instructions?)\??$/i.test(lower)) {
            intent = { type: 'dosage', drugName: drugFirstWord };
          } else if (/^(?:what\s+(?:does\s+it|is\s+it)|description|about|tell\s+me)\??$/i.test(lower)) {
            intent = { type: 'description', drugName: drugFirstWord };
          } else if (/^(?:miss(?:ed)?\s+(?:a\s+)?dose|what\s+if\s+I\s+miss)\??$/i.test(lower)) {
            intent = { type: 'missed_dose', drugName: drugFirstWord };
          } else if (/^(?:when|schedule|what\s+time)\??$/i.test(lower)) {
            intent = { type: 'schedule', drugName: drugFirstWord };
          }
        }
      }

      // Routing strategy:
      // 1. Fast-path intents → template (exact data, no AI needed)
      // 2. Everything else → RAG pipeline (unified RVF knowledge base)
      // 3. If RAG fails → try template response system
      const TEMPLATE_ONLY_INTENTS = new Set([
        'my_medications', 'schedule', 'next_dose', 'show_emr',
        'emr_diagnosis', 'emr_allergies', 'lab_question', 'emr_follow_up',
        'emr_discharge_info', 'help',
        'symptom_concern', 'emotional_concern', 'diet_question', 'otc_safe',
        'warning_signs', 'stop_taking', 'overall_status', 'hospitalization_reason',
      ]);

      const isTemplateIntent = TEMPLATE_ONLY_INTENTS.has(intent.type);

      // For non-template intents, route to RAG pipeline.
      // RAG has its own template tier for when LLM isn't ready yet.
      if (!isTemplateIntent) {
        try {
          // Get embedding for RAG search
          let queryVector: Float32Array | null = null;
          if (typeof embed === 'function') {
            queryVector = await embed(resolvedText);
          }

          if (queryVector) {
            // Show streaming typing indicator
            typingBubble.textContent = '';
            const typingDots = document.createElement('span');
            typingDots.textContent = t('chat.thinking');
            typingDots.style.cssText = 'color: var(--color-primary); font-style: italic;';
            typingBubble.appendChild(typingDots);

            let streamedText = '';
            let scrollTick = 0;
            const ragResult = await queryRAG(resolvedText, queryVector, (token) => {
              streamedText += token;
              typingDots.textContent = streamedText + '\u2588'; // block cursor
              // Scroll to bottom every 3 tokens to keep streaming response visible
              if (++scrollTick % 3 === 0) {
                messagesArea.scrollTop = messagesArea.scrollHeight;
              }
            }, intent.type);

            if (typingBubble.parentNode) typingBubble.parentNode.removeChild(typingBubble);

            // Add source attribution
            let responseText = ragResult.response;
            if (ragResult.sources.length > 0) {
              const sourceNames = ragResult.sources
                .map(s => s.replace(/^DRUG:\s*/i, '').replace(/^PATIENT RECORD — /i, '').replace(/^MEDICAL KNOWLEDGE — /i, '').split('\n')[0])
                .filter(s => s.length > 0)
                .slice(0, 3);
              if (sourceNames.length > 0) {
                responseText += `\n\n${t('chat.basedOn', { sources: sourceNames.join(', ') })}`;
              }
            }

            addMessage('assistant', responseText, false, ragResult.isLLMGenerated);

            // Log the AI query
            logAction('llm_query', JSON.stringify({ intent: intent.type, question: resolvedText.slice(0, 100) }));
            return;
          }
        } catch (ragErr) {
          console.warn('[Chat] RAG query failed, falling back to templates:', ragErr);
        }
      }

      // Try the structured template response system (handles 16 intent types)
      const templateResponse = buildTemplateResponse(intent.type as any, text);
      if (templateResponse) {
        if (typingBubble.parentNode) typingBubble.parentNode.removeChild(typingBubble);
        addMessage('assistant', templateResponse, false, false);
        logAction('template_response', JSON.stringify({ intent: intent.type, question: text.slice(0, 100) }));
        return;
      }

      // Final fallback: legacy response generator
      const response = await generateResponse(intent, patientMeds);

      if (typingBubble.parentNode) typingBubble.parentNode.removeChild(typingBubble);
      addMessage('assistant', response.text, response.hasWarning, true);
    } catch (err) {
      if (typingBubble.parentNode) typingBubble.parentNode.removeChild(typingBubble);
      addMessage('assistant', t('chat.error'), false, true);
      console.error('[Chat] Error generating response:', err);
    }

    input.focus();
  }

  // ---- Toast notification helper ----

  function showToast(message: string): void {
    const toast = document.createElement('div');
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.style.cssText = `
      position: fixed;
      bottom: calc(var(--nav-height) + var(--safe-bottom) + 80px);
      left: 50%;
      transform: translateX(-50%);
      padding: 10px 20px;
      border-radius: var(--radius-md);
      background: var(--color-text, #2d3748);
      color: #ffffff;
      font-size: var(--font-size-small);
      font-family: var(--font-family);
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 200;
      opacity: 0;
      transition: opacity 0.3s;
      text-align: center;
      max-width: 85%;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    // Fade in
    requestAnimationFrame(() => { toast.style.opacity = '1'; });
    // Fade out and remove after 3 seconds
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // ---- Event handlers ----

  async function handleSend(): Promise<void> {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    await processQuestion(text);
  }

  sendBtn.addEventListener('click', handleSend);
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  });

  // ---- Voice transcript listener ----
  // When STT produces a transcript (from mic button or other voice input),
  // process it through the chat system instead of the separate voice-responder.

  const onTranscript = (e: Event) => {
    const customEvent = e as CustomEvent<{ text: string }>;
    const text = customEvent.detail?.text?.trim();
    setMicState('idle');
    if (text) {
      // Prevent the global voice-responder from also handling this
      e.stopImmediatePropagation();
      processQuestion(text);
    } else {
      // STT produced no text — show feedback
      showToast(t('chat.voiceNotUnderstood'));
    }
  };

  // Listen with capture phase and stopImmediatePropagation to intercept before voice-responder
  document.addEventListener('aftercare:transcript', onTranscript, true);
  cleanups.push(() => document.removeEventListener('aftercare:transcript', onTranscript, true));

  // Also handle voice idle/error events to reset mic state
  const onVoiceIdle = () => { setMicState('idle'); };
  const onVoiceError = () => {
    setMicState('idle');
    showToast(t('chat.voiceNotUnderstood'));
  };
  document.addEventListener('aftercare:voice-idle', onVoiceIdle);
  document.addEventListener('aftercare:voice-error', onVoiceError);
  cleanups.push(() => {
    document.removeEventListener('aftercare:voice-idle', onVoiceIdle);
    document.removeEventListener('aftercare:voice-error', onVoiceError);
  });

  // Cleanup when navigating away
  const observer = new MutationObserver(() => {
    if (!container.isConnected) {
      cleanups.forEach(fn => fn());
      observer.disconnect();
    }
  });
  observer.observe(container.parentElement || document.body, { childList: true });

  input.focus();
}
